import { NavLink } from 'react-router-dom';
import { useApp } from '../state/AppState';
import { useI18n, type I18nKey } from '../i18n';
import { AstrallyMark } from './AstrallyMark';

/**
 * WHY THERE IS NO SEVENTH TAB (and no "Horoscope" tab at all).
 *
 * Six labelled destinations is the hard ceiling on a 360dp phone — a seventh
 * pushes each target under the 44px minimum and truncates every label. So the
 * daily horoscope, which is the single most-opened surface in the product, was
 * not given a tab. It was given the tab we already had:
 *
 *   **Home IS the horoscope surface.** Opening the app lands on today's
 *   reading — zero taps, which beats the one tap a dedicated tab would cost.
 *   The tab is therefore labelled "Today", because that is what it shows.
 *
 * Everything deeper hangs off Home in exactly one tap: `/horoscope` for the
 * week / month / year windows, `/muhurta` from the panchanga card, `/journal`
 * from the rate-your-reading card, `/compatibility` from the quick actions.
 * That is the trade — one tap for the weekly reading, zero for the daily one —
 * and it is the right way round, because the daily one is the habit.
 *
 * The alternative arrangements considered and rejected:
 *  - Drop Charts. It is the core utility people paid ₹49 for.
 *  - Drop Ally. It is the paid centrepiece and the reason Pro exists.
 *  - Add a tab only for private (social-off) accounts, who have two free slots.
 *    The bar would then gain and lose items when connections are switched on,
 *    which moves every remaining target under the user's thumb mid-session.
 *
 * WHY THE FEED REPLACES DISCOVER RATHER THAN JOINING IT.
 *
 * The feed exists only for social-enabled accounts, so it was a candidate for
 * a seventh tab — and a seventh tab is exactly what the note above rules out.
 * It is also not a candidate for the "add a tab only for some accounts" idea,
 * for the same reason: the COUNT would change when connections are switched on.
 *
 * So Feed takes Discover's slot. That keeps every arithmetic promise above
 * intact: six labelled destinations at most, and — the part that matters — the
 * bar has exactly as many items after this change as before it, in both
 * states. Switching connections on still swaps two tabs in and nothing moves
 * that was not already moving.
 *
 * Which of the two gets the slot is the real decision, and it is the same
 * argument that gave Home to the daily horoscope: the tab goes to the HABIT,
 * not to the more important feature. Discover is a deliberate, occasional act
 * — you open it when you are ready to spend one of a small number of
 * connection requests, and a Base account only ever gets five in a lifetime.
 * The feed is something you check. A tab is worth one tap on the thing you do
 * daily, not on the thing you do once a fortnight.
 *
 * Discover does not become hard to reach: it is one tap from the feed's own
 * header, and Home still links to it from "People aligned with you". That is
 * the same trade as the weekly horoscope — one tap for the occasional thing,
 * zero for the habitual one.
 */
interface TabDef {
  to: string;
  icon: string;
  /**
   * Null where no i18n key exists yet — see `label`. `t` is typed against
   * i18n/en.ts, so a key cannot simply be invented at the call site.
   */
  key: I18nKey | null;
  /** Literal fallback used only when `key` is null. */
  label?: string;
  social: boolean;
}

const ALL_TABS: TabDef[] = [
  // 'Today', not 'Home': this tab opens on the daily reading, and naming a tab
  // after its content is how a daily habit forms.
  { to: '/home', icon: 'mark', key: 'tabs.today' as const, social: false },
  { to: '/charts', icon: '☽', key: 'tabs.charts' as const, social: false },
  // 'Ally' rather than 'Astrally AI': six labels have to fit across a phone,
  // and Ally is what the assistant is called everywhere else in the copy.
  { to: '/ai', icon: '◈', key: 'tabs.ally' as const, social: false },
  // THIS SLOT WAS `/discover`. It is the feed now — same slot, same
  // social-only condition, so the bar has exactly as many items as it did
  // before, in both states. Discover keeps its route and is one tap away from
  // the feed's header; see the note above for why the habit got the tab.
  //
  // '❝' rather than another star: the feed is what people wrote, and every
  // other glyph in this bar is already celestial. `tabs.feed` belongs in
  // i18n/en.ts — that file was outside this change's scope, so the label is a
  // literal and `key: null` is the seam the key drops into.
  { to: '/feed', icon: '❝', key: null, label: 'Feed', social: true },
  { to: '/messages', icon: '✉', key: 'tabs.messages' as const, social: true },
  // Profile is a tab, not a hidden avatar tap. Everything a user configures
  // lives behind it — glossary, appearance, credits, Pro, memory, privacy,
  // and the switch that turns connections on — and none of that was
  // discoverable from a 44px unlabelled circle in the corner of Home.
  { to: '/profile', icon: '☺', key: 'tabs.profile' as const, social: false },
];

export function TabBar() {
  const { t } = useI18n();
  const { incomingConnections, conversations, uid, user } = useApp();
  const unread = conversations.reduce((s, c) => s + (c.unread?.[uid ?? ''] ?? 0), 0);
  const messagesBadge = incomingConnections.length + unread;

  // Private by default: social tabs simply do not exist until the user opts
  // in. Messages survives an opt-out while real conversations remain, so
  // nobody is abandoned mid-conversation.
  const socialOn = user?.socialEnabled === true;
  const tabs = ALL_TABS.filter(
    (tab) => !tab.social || socialOn || (tab.to === '/messages' && conversations.length > 0),
  );

  return (
    <nav className="tabbar">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={({ isActive }) => `tabbar__item${isActive ? ' tabbar__item--active' : ''}`}
        >
          <span className="icon">
            {tab.icon === 'mark' ? (
              <AstrallyMark size={19} color="currentColor" lagnaColor={null} weight={5} />
            ) : (
              tab.icon
            )}
          </span>
          <span>{tab.key ? t(tab.key) : tab.label}</span>
          {tab.to === '/messages' && messagesBadge > 0 && <span className="tabbar__dot" />}
        </NavLink>
      ))}
    </nav>
  );
}
